<template>
  <confirm-dialog ref="emailConfirm" :hideOverlay="hideOverlay">
    <template #checkbox>
      <email-users-list
        v-if="openUserList"
        :userlist="userlist"
        v-model="selectedUsers"
      >
      </email-users-list>
      <div class="error-element mt-2" v-if="emailListValidation">
        {{ emailListValidation }}
      </div>
      <div class="mt-4" v-if="newDocuments.length">
        <span>{{ $t("agenda.lblNewlyDocument") }}</span>
        <email-document-list
          :documentlist="newDocuments"
          v-model="selectedDocuments"
        ></email-document-list>
      </div>
      <v-textarea
        v-model.trim="txtComment"
        prepend-icon="mdi-comment"
        class="mt-4"
        :label="$t('agenda.lblComment')"
        hide-details=""
        auto-grow
        :row-height="24"
        :rows="1"
      >
      </v-textarea>
    </template>
  </confirm-dialog>
</template>

<script>
import ConfirmDialog from "../ConfirmDialog.vue";
import EmailUsersList from "./EmailUsersList.vue";
import { Internationalization } from "@syncfusion/ej2-base";
import EmailDocumentList from "./EmailDocumentList.vue";
import { mapActions } from "vuex";

import { cloneDeep, orderBy, compact } from "lodash";
export default {
  name: "EmailSendConfirmation",
  components: { ConfirmDialog, EmailUsersList, EmailDocumentList },
  props: {
    emailData: {
      type: Object,
      default: () => {},
      required: true,
    },
    notificationMessage: { type: String, default: "" },
  },
  data() {
    return {
      txtComment: "",
      selectedUsers: [],
      selectedDocuments: [],
      intl: new Internationalization(),
      hideOverlay: false,
      userEmailList: [],
      templateVariables: {},
      openUserList: false,
      emailListValidation: null,
    };
  },
  computed: {
    meetingDetails() {
      return this.emailData.meetingDetails || null;
    },
    selectedLang() {
      return this.$i18n.locale;
    },
    userlist() {
      if (this.meetingDetails) {
        const attendeesList = cloneDeep(this.meetingDetails.attendees);
        let emailData = attendeesList.map((r) => {
          const contactEmailIds = [];
          if (r.contactEmailIds && r.contactEmailIds.length > 0) {
            r.contactEmailIds.forEach((email) => {
              if (email != "" && r.email != email) {
                contactEmailIds.push(email);
              }
            });
          }

          return {
            ...r,
            contactEmailIds: contactEmailIds,
            email: r.email,
            name: r.firstName ? `${r.firstName} ${r.lastName}` : r.email,
          };
        });
        return orderBy(emailData, [(user) => user.name.toLowerCase()], ["asc"]);
      }
      return [];
    },
    newDocuments() {
      return this.emailData.newDocuments || [];
    },
    selectedTenant() {
      return sessionStorage.getItem("tenantId");
    },
    tenantName() {
      const users = this.$auth.user[process.env.VUE_APP_TENANT_KEY];
      const tenant = users.find((u) => u.id === this.selectedTenant);
      if (tenant) {
        return tenant.name;
      }
      return "";
    },
    sendgridVars() {
      let subject = this.$t("email.emailMailSubject");
      let title = this.$t("email.emailNotificationTitle");
      let message = this.$t("email.message", {
        meetingName: this.meetingDetails.meetingName,
      });
      if (this.emailData.type === "DeleteMeeting") {
        subject = this.$t("email.emailMailDeleteSubject");
        title = this.$t("email.emailNotificationDeleteTitle");
        message = this.$t("email.messageDelete", {
          meetingName: this.meetingDetails.meetingName,
        });
      }
      return {
        ...this.templateVariables,
        ...{
          message: message,
          emailMailSubject: subject,
          emailNotificationTitle: title,
          message010: this.$t("email.message010"),
          emailCaptionTitle: this.$t("email.emailCaptionTitle"),
          emailCaptionMeetingType: this.$t("email.emailCaptionMeetingType"),
          emailCaptionSchedule: this.$t("email.emailCaptionSchedule"),
          emailCaptionLocation: this.$t("email.emailCaptionLocation"),
          emailCaptionCompany: this.$t("email.emailCaptionCompany"),
          message020: this.$t("email.message020"),
          message030: this.$t("email.message030"),
          emailOpenMeeting: this.$t("email.emailOpenMeeting"),
          emailSupportML: this.$t("email.emailSupportML"),
          emailPreHeader: this.$t("email.emailPreHeader"),
        },
      };
    },
  },
  watch: {
    emailData: {
      immediate: true,
      deep: true,
      async handler() {
        if (this.emailData.open) {
          this.hideOverlay = this.emailData.hideOverlay || false;
          this.openEmailConfirmationDialog();
        }
      },
    },
    selectedUsers(value) {
      if (value.length === 0) {
        this.$refs.emailConfirm.$refs.primaryButton.$el.classList.add(
          "v-btn--disabled"
        );
        this.$refs.emailConfirm.$refs.primaryButton.$el.setAttribute(
          "disabled",
          "disabled"
        );
        this.emailListValidation = this.$t("reqToListRule");
      } else {
        if (this.$refs.emailConfirm.$refs.primaryButton) {
          this.$refs.emailConfirm.$refs.primaryButton.$el.classList.remove(
            "v-btn--disabled"
          );
          this.$refs.emailConfirm.$refs.primaryButton.$el.removeAttribute(
            "disabled"
          );
        }
        this.emailListValidation = null;
      }
    },
  },
  methods: {
    ...mapActions({
      getUserByEmailId: "admin/getUserByEmailId",
    }),
    openEmailConfirmationDialog,
    createPersonalisedData,
    getFormattedMeetingTime,
    sendMail,
    updateEmailMessage,
    getDynamicVariables,
    getAlternateEmailIds,
    getUserEmailDetails,
  },
};

async function getUserEmailDetails() {
  const emailsList = cloneDeep(this.userlist);
  const alternateEmails = [];
  for (const value of emailsList) {
    let alternateEmailsIds = await this.getAlternateEmailIds(value.email);
    alternateEmailsIds.forEach((email) => {
      alternateEmails.push({
        email: email,
        name: value.name,
        profielPhoto: value.profielPhoto,
      });
    });
  }
  const emailList = emailsList.concat(alternateEmails);
  return emailList;
}

async function getAlternateEmailIds(email) {
  let contactEmailIds = [];
  await this.getUserByEmailId(email.toLowerCase().trim())
    .then((response) => {
      let user = response;
      if (user.contactEmailIds) {
        contactEmailIds = user.contactEmailIds;
      }
    })
    .catch((error) => {});

  return contactEmailIds;
}

async function openEmailConfirmationDialog() {
  this.selectedUsers = [...this.userlist];
  this.selectedDocuments = [...this.newDocuments];
  this.txtComment = "";
  this.openUserList = true;
  const result = await this.$refs.emailConfirm.open(
    this.$t("agenda.lblMeetingUpdate"),
    this.$t("agenda.msgConfirmNotification"),
    {
      sendEmailButtons: true,
      width: 900,
    }
  );
  if (result) {
    this.emailListValidation = null;
    let toList = [];
    let toListData = [];
    this.selectedUsers.forEach((value) => {
      let name = `${value.firstName} ${value.lastName}`;
      if (value.name) {
        name = value.name;
      }

      toList.push([value.email, name]);
      toListData.push(value.email);
      if (value.contactEmailIds && value.contactEmailIds.length > 0) {
        value.contactEmailIds.forEach((email) => {
          if (email) {
            toList.push([email, name]);
            toListData.push(email);
          }
        });
      }
    });
    let personalisedData = {};
    if (this.emailData.isPersonalised) {
      personalisedData = toListData.reduce(
        (a, v) => ({
          ...a,
          [v]: this.createPersonalisedData(this.meetingDetails, v),
        }),
        {}
      );
    }
    let fileList = [];
    const emailTemplateData = {
      toList,
      isPersonalised: this.emailData.isPersonalised,
      personalisedData,
      fileList,
      dynamicVariables: this.getDynamicVariables(),
    };
    this.sendMail(emailTemplateData);
  } else {
    this.openUserList = false;
    this.$emit("cancel");
  }
}

function getDynamicVariables() {
  return {
    ...this.sendgridVars,
    ...{
      emailDearRecipient: this.$t("email.emailDearRecipient", {
        username: this.$auth.user.name,
      }),
      meetingTitle: this.meetingDetails.meetingName,
      meetingType: this.meetingDetails.meetingType,
      schedule: this.getFormattedMeetingTime(this.meetingDetails),
      meetingLocation: this.meetingDetails.location,
      meetingId: this.meetingDetails.id,
      username: this.$auth.user.tenantUserName,
      documents: this.selectedDocuments,
      comment: this.txtComment,
      tenantName: this.tenantName,
      meetingTypeId: this.meetingDetails.meetingTypeId,
    },
  };
}

function updateEmailMessage() {
  if (
    this.emailData.type === "SaveMeeting" ||
    this.emailData.type === "UpdateMeeting"
  ) {
    return this.$t("agenda.MeetingSaveUpdate");
  }
  if (this.emailData.type === "SaveMeetingDetails") {
    return this.$t("agenda.MeetingWithNameUpdated", {
      meeting: this.meetingDetails.meetingName,
    });
  }
  if (this.emailData.type === "DeleteMeeting") {
    return this.$t("agenda.MeetingWithNameDeleted", {
      meeting: this.meetingDetails.meetingName,
    });
  }
  return this.$t("agenda.msgNotDefine");
}

function sendMail(emailTemplateData) {
  this.$notification
    .sendMail(emailTemplateData, this.emailData.type)
    .then((response) => {
      this.$emit("send", true);
    })
    .catch((error) => {
      this.$emit("send", false);
    });
}

function createPersonalisedData(meetingDetails, email) {
  const meetingTime =
    this.intl.formatDate(new Date(meetingDetails.scheduletStartTimeInUTC), {
      type: "date",
      skeleton: "long",
    }) +
    " " +
    this.intl.formatDate(new Date(meetingDetails.scheduletStartTimeInUTC), {
      skeleton: "hm",
    }) +
    " - " +
    this.intl.formatDate(new Date(meetingDetails.scheduledEndTimeInUTC), {
      type: "date",
      skeleton: "long",
    }) +
    " " +
    this.intl.formatDate(new Date(meetingDetails.scheduledEndTimeInUTC), {
      skeleton: "hm",
    });

  const user = this.selectedUsers.find((u) => u.email === email);
  const username = user
    ? user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.name || email
    : email;

  return {
    ...this.sendgridVars,
    ...{
      emailDearRecipient: this.$t("email.emailDearRecipient", {
        username: username,
      }),
      meetingTitle: meetingDetails.meetingName,
      meetingType: meetingDetails.meetingType,
      schedule: meetingTime,
      meetingLocation: meetingDetails.location,
      meetingId: meetingDetails.id,
      username,
      documents: this.selectedDocuments,
      comment: this.txtComment,
      tenantName: this.tenantName,
    },
  };
}

function getFormattedMeetingTime(meetingDetails) {
  let meetingTime =
    this.intl.formatDate(new Date(meetingDetails.scheduletStartTimeInUTC), {
      type: "date",
      skeleton: "long",
    }) +
    " " +
    this.intl.formatDate(new Date(meetingDetails.scheduletStartTimeInUTC), {
      skeleton: "hm",
    }) +
    " - " +
    this.intl.formatDate(new Date(meetingDetails.scheduledEndTimeInUTC), {
      type: "date",
      skeleton: "long",
    }) +
    " " +
    this.intl.formatDate(new Date(meetingDetails.scheduledEndTimeInUTC), {
      skeleton: "hm",
    });
  return meetingTime;
}
</script>

<style scoped>
.error-element {
  color: var(--v-error-base) !important;
}
</style>